import { Button } from 'antd'
import { FC } from 'react'
import styled from 'styled-components'
import type { DeltaStatic } from 'quill'
import classNames from 'classnames'
import { useTranslation } from 'next-i18next'

import { poppins } from '@/components/common/fonts'
import { IconAudioScript, IconTextScript } from '@/components/common/Icons'
import { Gender } from '@/constants/gender'
import { Model } from '@/types/models'
import {
  SCRIPT_TYPE,
  ScriptType,
  TextScriptTempo,
} from '@/constants/tools/solid-background-video'

import { TextScript } from './TextScript'
import { AudioScript } from './AudioScript'
import { VoiceButton } from './VoiceButton'

export const Script: FC<{
  scriptType: ScriptType
  onChangeScriptType: (scriptType: ScriptType) => void

  textScript?: DeltaStatic
  onChangeTextScript: ({}: {
    html: string
    delta: DeltaStatic
    plainText: string
  }) => void
  onPasteTextScript: () => void

  tempo: TextScriptTempo
  onChangeTempo: (tempo: TextScriptTempo) => void

  dictionary: Record<string, string>
  onChangeDictionary: (dictionary: Record<string, string>) => void

  audioScriptUrl?: string
  audioScriptFile?: File
  onChangeAudioScriptFile: (audioScriptFile?: File) => void

  voice?: {
    modelId: string
    voiceId: string
    language: string
    gender: Gender
    isOriginalVoice: boolean
    type: string
  }
  onChangeVoice: (voice: {
    modelId: string
    voiceId: string
    language: string
    gender: Gender
    isOriginalVoice: boolean
    type: string
  }) => void
  models?: Model[]
  ttsList?: Tts[]

  onGeneratePreviewAudio: () => void
  disabledGeneratePreviewAudio: boolean
  isGeneratingPreviewAudio: boolean
}> = ({
  scriptType,
  onChangeScriptType,

  textScript,
  onChangeTextScript,
  onPasteTextScript,

  tempo,
  onChangeTempo,

  dictionary,
  onChangeDictionary,

  audioScriptUrl,
  audioScriptFile,
  onChangeAudioScriptFile,

  voice,
  onChangeVoice,
  models = [],
  ttsList = [],

  onGeneratePreviewAudio,
  disabledGeneratePreviewAudio,
  isGeneratingPreviewAudio,
}) => {
  const { t } = useTranslation('common')

  return (
    <ScriptContainer>
      <TabsContainer>
        <Tabs>
          <Tab
            onClick={() => onChangeScriptType(SCRIPT_TYPE.TEXT)}
            className={classNames({
              selected: scriptType === SCRIPT_TYPE.TEXT,
            })}
          >
            <IconTextScript />
            {t('Text Script')}
          </Tab>
          <Tab
            onClick={() => onChangeScriptType(SCRIPT_TYPE.AUDIO)}
            className={classNames({
              selected: scriptType === SCRIPT_TYPE.AUDIO,
            })}
          >
            <IconAudioScript />
            {t('Audio Script')}
          </Tab>
        </Tabs>

        <TabsAppendix>
          {scriptType === SCRIPT_TYPE.TEXT && voice && (
            <VoiceButton
              voice={voice}
              onChangeVoice={onChangeVoice}
              models={models}
              ttsList={ttsList}
            />
          )}
        </TabsAppendix>
      </TabsContainer>

      <Content>
        {scriptType === SCRIPT_TYPE.TEXT && (
          <TextScript
            textScript={textScript}
            onChangeTextScript={onChangeTextScript}
            onPasteTextScript={onPasteTextScript}
            tempo={tempo}
            onChangeTempo={onChangeTempo}
            dictionary={dictionary}
            onChangeDictionary={onChangeDictionary}
            onGenerate={onGeneratePreviewAudio}
            disabledGenerate={disabledGeneratePreviewAudio}
            loadingGenerate={isGeneratingPreviewAudio}
          />
        )}
        {scriptType === SCRIPT_TYPE.AUDIO && (
          <AudioScript
            audioScriptUrl={audioScriptUrl}
            audioScriptFile={audioScriptFile}
            onChangeAudioScriptFile={onChangeAudioScriptFile}
          />
        )}
      </Content>
    </ScriptContainer>
  )
}

const ScriptContainer = styled.div`
  display: flex;
  flex-direction: column;

  height: 100%;
`

const TabsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  height: 46px;
`

const Tabs = styled.div`
  align-self: end;

  display: flex;
`

const Tab = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  min-width: 130px;
  max-width: 200px;
  height: 40px;
  padding: 0;
  padding-inline: 8px;

  border-radius: 0;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;

  border: 1px solid #f4f4f4;
  border-bottom: 0;

  background-color: #f4f4f4;
  box-shadow: none;

  font-family: ${poppins.string};
  font-size: 14px;
  line-height: 20px;
  color: #777777;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  svg {
    flex-shrink: 0;
  }
  svg path {
    stroke: currentColor;
  }

  &&&:hover {
    color: #777777;
    background-color: #f4f4f4;
    border-color: black;
  }

  &&&.selected {
    border-color: #cccccc;
    background-color: #ffffff;
    color: #222222;
    font-weight: 500;

    &&&:hover {
      color: #222222;
      background-color: #ffffff;
      opacity: 1;
    }
  }
`

const TabsAppendix = styled.div`
  flex: 1;
  align-self: stretch;

  display: flex;
  justify-content: end;
  align-items: center;

  border-bottom: 1px solid #cccccc;
`

const Content = styled.div`
  flex: 1;

  overflow: hidden;
`
